import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import CloseButton from '../Buttons/CloseButton/CloseButton';
import CustomImage from '../CustomImage/CustomImage';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import type { SavedItinerary } from '@/api/itinerary';
import type { PlaceCardData } from '@/types/listViewTypes';
import { normalizePlaceTextForFingerprint } from '@/utils/placeFingerprint';
import arrowBackIcon from '@/assets/arrow-back.svg';

export interface SaveDestinationSelection {
  destinationType: 'itinerary' | 'list';
  destinationId: string;
  destinationName: string;
  isNew: boolean;
  seedPlaceId?: string;
}

type ActiveTab = 'itineraries' | 'lists';
type NamingFor = 'itinerary' | 'list' | null;

function buildCandidateKeys(place: PlaceCardData | null | undefined): Set<string> {
  const keys = new Set<string>();
  if (!place) return keys;
  const googleId = (place.googlePlaceId ?? place.placeId ?? '').trim();
  if (googleId) keys.add(`g:${googleId}`);
  const locationId = (place.locationId ?? '').trim();
  if (locationId) keys.add(`m:${locationId}`);
  const title = normalizePlaceTextForFingerprint(place.title);
  const address = normalizePlaceTextForFingerprint(place.address);
  if (title) keys.add(`u:${title}|${address}`);
  return keys;
}

function formatCreatedDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
  } catch {
    return '';
  }
}

export interface SaveDestinationPickerPanelProps {
  variant: 'overlay' | 'inline';
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  onBack?: () => void;
  savedItineraries?: SavedItinerary[];
  savedLists?: SavedItinerary[];
  isLoading?: boolean;
  isLoadingLists?: boolean;
  place?: PlaceCardData | null;
  /** When false, resets tab/search/naming state. */
  active?: boolean;
  controlsLocked?: boolean;
  closeButtonDisabled?: boolean;
  tabPanelPrefix?: React.ReactNode;
  blockingOverlay?: React.ReactNode;
  onPickItinerary: (itinerary: SavedItinerary) => void | Promise<void>;
  onPickList: (list: SavedItinerary) => void | Promise<void>;
  onCreateNewItinerary: (title: string) => void | Promise<void>;
  onCreateNewList: (title: string) => void | Promise<void>;
  /** Prefill when user taps create new list (e.g. trip import review title). */
  suggestedNewListTitle?: string;
  /** Prefill when user taps create new itinerary. */
  suggestedNewItineraryTitle?: string;
  className?: string;
}

const SaveDestinationPickerPanel: React.FC<SaveDestinationPickerPanelProps> = ({
  variant,
  title,
  subtitle,
  onClose,
  onBack,
  savedItineraries = [],
  savedLists = [],
  isLoading = false,
  isLoadingLists = false,
  place,
  active = true,
  controlsLocked = false,
  closeButtonDisabled = false,
  tabPanelPrefix,
  blockingOverlay,
  onPickItinerary,
  onPickList,
  onCreateNewItinerary,
  onCreateNewList,
  suggestedNewListTitle,
  suggestedNewItineraryTitle,
  className = '',
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('itineraries');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreatingItinerary, setIsCreatingItinerary] = useState(false);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [isSavingToItinerary, setIsSavingToItinerary] = useState(false);
  const [isSavingToList, setIsSavingToList] = useState(false);
  const [namingFor, setNamingFor] = useState<NamingFor>(null);
  const [nameInput, setNameInput] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const listPanelScrollRef = useRef<HTMLDivElement>(null);
  const listPanelSentinelRef = useRef<HTMLDivElement>(null);
  const [showListPanelScrollHint, setShowListPanelScrollHint] = useState(false);

  useEffect(() => {
    if (active) return;
    setSearchQuery('');
    setSearchOpen(false);
    setIsSavingToItinerary(false);
    setIsSavingToList(false);
    setNamingFor(null);
    setNameInput('');
    setActiveTab('itineraries');
  }, [active]);

  useEffect(() => {
    if (searchOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [searchOpen]);

  useEffect(() => {
    if (namingFor) {
      requestAnimationFrame(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      });
    }
  }, [namingFor]);

  const filteredItineraries = savedItineraries.filter((it) =>
    it.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredLists = savedLists.filter((l) =>
    l.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const candidateKeys = useMemo(() => buildCandidateKeys(place), [place]);

  const duplicateItineraryIds = useMemo(() => {
    const ids = new Set<string>();
    for (const it of savedItineraries) {
      if (it.placeFingerprintKeys?.some((k) => candidateKeys.has(k))) {
        ids.add(it.id);
      }
    }
    return ids;
  }, [savedItineraries, candidateKeys]);

  const duplicateListIds = useMemo(() => {
    const ids = new Set<string>();
    for (const l of savedLists) {
      if (l.placeFingerprintKeys?.some((k) => candidateKeys.has(k))) {
        ids.add(l.id);
      }
    }
    return ids;
  }, [savedLists, candidateKeys]);

  const isCreating = isCreatingItinerary || isCreatingList;
  const isActiveOperation = isSavingToItinerary || isSavingToList || isCreating;
  const locked = controlsLocked || isActiveOperation;
  const namingOpen = !!namingFor;

  const updateListPanelScrollHint = useCallback((): void => {
    const root = listPanelScrollRef.current;
    const sentinel = listPanelSentinelRef.current;
    if (!root || !sentinel) return;

    const hasOverflow = root.scrollHeight > root.clientHeight + 2;
    if (!hasOverflow) {
      setShowListPanelScrollHint(false);
      return;
    }

    const rootRect = root.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    const sentinelVisible =
      sentinelRect.bottom > rootRect.top && sentinelRect.top < rootRect.bottom;
    setShowListPanelScrollHint(!sentinelVisible);
  }, []);

  useEffect(() => {
    if (!active || namingOpen) {
      setShowListPanelScrollHint(false);
      return;
    }

    const root = listPanelScrollRef.current;
    if (!root) return;

    updateListPanelScrollHint();
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(updateListPanelScrollHint);
    });

    const resizeObserver = new ResizeObserver(() => {
      updateListPanelScrollHint();
    });
    resizeObserver.observe(root);
    root.addEventListener('scroll', updateListPanelScrollHint, { passive: true });

    return () => {
      window.cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      root.removeEventListener('scroll', updateListPanelScrollHint);
    };
  }, [
    active,
    activeTab,
    filteredItineraries.length,
    filteredLists.length,
    isLoading,
    isLoadingLists,
    namingOpen,
    searchQuery,
    tabPanelPrefix,
    updateListPanelScrollHint,
  ]);

  const handleAddClick = async (e: React.MouseEvent, itinerary: SavedItinerary) => {
    e.stopPropagation();
    if (locked) return;

    setIsSavingToItinerary(true);
    try {
      await onPickItinerary(itinerary);
    } finally {
      setIsSavingToItinerary(false);
    }
  };

  const handleAddListClick = async (e: React.MouseEvent, list: SavedItinerary) => {
    e.stopPropagation();
    if (locked) return;

    setIsSavingToList(true);
    try {
      await onPickList(list);
    } finally {
      setIsSavingToList(false);
    }
  };

  const handleNewItineraryClick = () => {
    if (!place || locked) return;
    setNamingFor('itinerary');
    const suggested = suggestedNewItineraryTitle?.trim();
    setNameInput(suggested || (place.title ? `${place.title} Itinerary` : 'New Itinerary'));
  };

  const handleNewListClick = () => {
    if (!place || locked) return;
    setNamingFor('list');
    const suggested = suggestedNewListTitle?.trim();
    setNameInput(suggested || (place.title ? `${place.title} List` : 'New List'));
  };

  const handleNamingCancel = () => {
    setNamingFor(null);
    setNameInput('');
  };

  const handleNamingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!place || !namingFor) return;

    const trimmedName = nameInput.trim();

    if (namingFor === 'itinerary') {
      const itineraryTitle = trimmedName || 'New Itinerary';
      setIsCreatingItinerary(true);
      try {
        await onCreateNewItinerary(itineraryTitle);
        setNamingFor(null);
        setNameInput('');
      } finally {
        setIsCreatingItinerary(false);
      }
    } else {
      const listTitle = trimmedName || 'New List';
      setIsCreatingList(true);
      try {
        await onCreateNewList(listTitle);
        setNamingFor(null);
        setNameInput('');
      } finally {
        setIsCreatingList(false);
      }
    }
  };

  const rootClasses =
    variant === 'overlay'
      ? 'bg-white rounded-2xl mx-auto w-full max-w-[500px] max-h-[90vh] flex flex-col shadow-[0_20px_60px_rgba(0,0,0,0.3)] font-manrope relative z-[1001] overflow-hidden isolate'
      : 'flex min-h-0 flex-1 flex-col font-manrope overflow-hidden';

  return (
    <div
      data-gt-save-to-overlay-root
      className={`${rootClasses} ${className}`.trim()}
    >
      {blockingOverlay}

      <div
        className={
          variant === 'inline'
            ? 'flex shrink-0 items-center justify-between gap-3 pb-4'
            : 'flex items-center justify-between gap-3 px-6 pt-6 pb-4'
        }
      >
        {variant === 'inline' ? (
          <button
            type="button"
            onClick={onBack}
            className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center border-0 bg-transparent p-0"
            aria-label="Go back"
          >
            <img src={arrowBackIcon} alt="" width={24} height={24} />
          </button>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 className="m-0 min-w-0 truncate text-base font-semibold leading-snug tracking-[0.02em] text-[#252528]">
            {title ?? 'Save to Trip or List'}
          </h2>
          {subtitle ? (
            <p className="m-0 truncate text-sm font-normal leading-snug text-[#525257]">
              {subtitle}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {!namingOpen ? (
            <button
              type="button"
              aria-label={searchOpen ? 'Close search' : 'Search'}
              aria-expanded={searchOpen}
              disabled={locked}
              onClick={() => setSearchOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E1E1E7] text-[#252528] transition-opacity hover:bg-[#d4d4db] disabled:opacity-40"
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}

          {!namingOpen ? (
            activeTab === 'itineraries' ? (
              <button
                type="button"
                aria-label="Create new itinerary"
                disabled={locked}
                onClick={handleNewItineraryClick}
                className="flex items-center gap-2 rounded-xl bg-[#E1E1E7] px-2 py-1 text-base font-semibold tracking-[0.02em] text-[#252528] transition-opacity hover:bg-[#d4d4db] disabled:opacity-40"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#252528]">
                  <AddIcon sx={{ fontSize: 14, color: '#fff' }} />
                </span>
                <span>New</span>
              </button>
            ) : (
              <button
                type="button"
                aria-label="Create new list"
                disabled={locked}
                onClick={handleNewListClick}
                className="flex items-center gap-2 rounded-xl bg-[#E1E1E7] px-2 py-1 text-base font-semibold tracking-[0.02em] text-[#252528] transition-opacity hover:bg-[#d4d4db] disabled:opacity-40"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#252528]">
                  <AddIcon sx={{ fontSize: 14, color: '#fff' }} />
                </span>
                <span>New</span>
              </button>
            )
          ) : null}

          {variant === 'overlay' ? (
            <CloseButton
              onClick={onClose}
              ariaLabel="Close save to overlay"
              size={24}
              disabled={closeButtonDisabled || isActiveOperation}
            />
          ) : (
            <span className="h-6 w-6 shrink-0" aria-hidden />
          )}
        </div>
      </div>

      {namingOpen ? (
        <form
          onSubmit={(e) => void handleNamingSubmit(e)}
          className={`flex flex-col gap-3 pb-5 ${variant === 'inline' ? '' : 'px-6'}`}
        >
          <label
            htmlFor="new-item-name"
            className="text-sm font-medium tracking-[0.02em] text-[#252528]"
          >
            {namingFor === 'itinerary' ? 'Name your itinerary' : 'Name your list'}
          </label>
          <input
            id="new-item-name"
            ref={nameInputRef}
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder={namingFor === 'itinerary' ? 'New Itinerary' : 'New List'}
            disabled={isCreating}
            className="h-9 w-full rounded-xl border border-[#b3b3b3] bg-white px-3 text-sm text-[#252528] placeholder-[#a7a7ae] outline-none focus:border-[#371168] disabled:opacity-40"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleNamingCancel}
              disabled={isCreating}
              className="rounded-xl bg-[#E1E1E7] px-4 py-2 text-sm font-medium text-[#525257] transition-opacity hover:bg-[#d4d4db] disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="flex items-center gap-2 rounded-xl bg-[#371168] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:bg-[#250d4a] disabled:opacity-40"
            >
              {isCreating ? <LoadingSpinner size="small" /> : null}
              <span>Create</span>
            </button>
          </div>
        </form>
      ) : null}

      {!namingOpen && searchOpen ? (
        <div className={variant === 'inline' ? 'pb-3' : 'px-6 pb-3'}>
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={locked}
            className="h-9 w-full rounded-xl border border-[#b3b3b3] bg-white px-3 text-sm text-[#252528] placeholder-[#a7a7ae] outline-none focus:border-[#371168] disabled:opacity-40"
          />
        </div>
      ) : null}

      {!namingOpen ? (
        <div
          role="tablist"
          aria-label="Save destination type"
          className={`flex items-center border-b border-[#e1e1e7] ${variant === 'inline' ? '' : 'px-6'}`}
        >
          <button
            role="tab"
            aria-selected={activeTab === 'itineraries'}
            aria-controls="save-tab-panel"
            id="tab-itineraries"
            type="button"
            disabled={locked}
            onClick={() => setActiveTab('itineraries')}
            className={`border-b-2 px-4 py-3 font-manrope text-base tracking-[0.02em] transition-colors disabled:opacity-40 ${
              activeTab === 'itineraries'
                ? 'border-[#371168] font-semibold text-[#252528]'
                : 'border-transparent font-normal text-[#525257] hover:text-[#252528]'
            }`}
          >
            Trips
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'lists'}
            aria-controls="save-tab-panel"
            id="tab-lists"
            type="button"
            disabled={locked}
            onClick={() => setActiveTab('lists')}
            className={`border-b-2 px-4 py-3 font-manrope text-base tracking-[0.02em] transition-colors disabled:opacity-40 ${
              activeTab === 'lists'
                ? 'border-[#371168] font-semibold text-[#252528]'
                : 'border-transparent font-normal text-[#525257] hover:text-[#252528]'
            }`}
          >
            Lists
          </button>
        </div>
      ) : null}

      {!namingOpen ? (
        <div
          ref={listPanelScrollRef}
          id="save-tab-panel"
          role="tabpanel"
          aria-labelledby={activeTab === 'itineraries' ? 'tab-itineraries' : 'tab-lists'}
          className="gt-keyboard-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]"
        >
          {tabPanelPrefix}

          {(isSavingToItinerary || isSavingToList) && !tabPanelPrefix ? (
            <div className="flex items-center gap-2 px-6 py-3 text-sm text-[#525257]">
              <LoadingSpinner size="small" />
              <span>Saving…</span>
            </div>
          ) : null}

          {activeTab === 'itineraries' ? (
            <div
              className={`flex flex-col py-2 ${locked ? 'pointer-events-none select-none opacity-50' : ''}`}
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm text-[#a7a7ae]">
                  <LoadingSpinner />
                  <span>Loading itineraries…</span>
                </div>
              ) : filteredItineraries.length > 0 ? (
                filteredItineraries.map((itinerary) => {
                  const createdLabel = formatCreatedDate(itinerary.created_at);
                  const alreadyAdded = duplicateItineraryIds.has(itinerary.id);
                  return (
                    <div key={itinerary.id} className="flex items-center gap-4 px-6 py-2">
                      <div className="relative h-10 w-10 min-w-[40px] shrink-0 overflow-hidden rounded-lg bg-[#E1E1E7] [&>div]:h-full [&>div]:min-h-0 [&>div]:w-full">
                        <CustomImage
                          src={itinerary.imageUrl}
                          alt=""
                          fill
                          sizes="40px"
                          fallbackSources={
                            itinerary.imageUrlFallback ? [itinerary.imageUrlFallback] : undefined
                          }
                          fallbackSrc="/images/default_post.png"
                          lazy={false}
                          style={{ objectFit: 'cover', objectPosition: 'center' }}
                        />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-sm font-normal leading-snug tracking-[0.02em] text-[#252528]">
                          {itinerary.name}
                        </span>
                        {createdLabel ? (
                          <span className="text-xs tracking-[0.02em] text-[#a7a7ae]">
                            Created {createdLabel}
                          </span>
                        ) : null}
                      </div>
                      {alreadyAdded ? (
                        <div
                          aria-label={`Already in ${itinerary.name}`}
                          title={`Already in ${itinerary.name}`}
                          className="flex h-6 w-6 shrink-0 cursor-default items-center justify-center rounded-full bg-[#252528]"
                        >
                          <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
                        </div>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Add to ${itinerary.name}`}
                          onClick={(e) => void handleAddClick(e, itinerary)}
                          className="flex h-6 w-6 shrink-0 items-center justify-center text-[#252528] transition-colors hover:text-[#371168] active:scale-95 disabled:opacity-40"
                          disabled={locked}
                        >
                          <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
                        </button>
                      )}
                    </div>
                  );
                })
              ) : searchQuery ? (
                <div className="flex items-center justify-center py-10 text-sm text-[#a7a7ae]">
                  <span>No itineraries found</span>
                </div>
              ) : (
                <div className="flex items-center justify-center py-10 text-sm text-[#a7a7ae]">
                  <span>No itineraries yet. Create your first one!</span>
                </div>
              )}
            </div>
          ) : null}

          {activeTab === 'lists' ? (
            <div
              className={`flex flex-col py-2 ${locked ? 'pointer-events-none select-none opacity-50' : ''}`}
            >
              {isLoadingLists ? (
                <div className="flex flex-col items-center justify-center gap-3 py-10 text-sm text-[#a7a7ae]">
                  <LoadingSpinner />
                  <span>Loading lists…</span>
                </div>
              ) : filteredLists.length > 0 ? (
                filteredLists.map((list) => {
                  const createdLabel = formatCreatedDate(list.created_at);
                  const alreadyAdded = duplicateListIds.has(list.id);
                  return (
                    <div key={list.id} className="flex items-center gap-4 px-6 py-2">
                      <div className="relative h-10 w-10 min-w-[40px] shrink-0 overflow-hidden rounded-lg bg-[#E1E1E7] [&>div]:h-full [&>div]:min-h-0 [&>div]:w-full">
                        <CustomImage
                          src={list.imageUrl}
                          alt=""
                          fill
                          sizes="40px"
                          fallbackSources={list.imageUrlFallback ? [list.imageUrlFallback] : undefined}
                          fallbackSrc="/images/default_post.png"
                          lazy={false}
                          style={{ objectFit: 'cover', objectPosition: 'center' }}
                        />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate text-sm font-normal leading-snug tracking-[0.02em] text-[#252528]">
                          {list.name}
                        </span>
                        {createdLabel ? (
                          <span className="text-xs tracking-[0.02em] text-[#a7a7ae]">
                            Created {createdLabel}
                          </span>
                        ) : null}
                      </div>
                      {alreadyAdded ? (
                        <div
                          aria-label={`Already in ${list.name}`}
                          title={`Already in ${list.name}`}
                          className="flex h-6 w-6 shrink-0 cursor-default items-center justify-center rounded-full bg-[#252528]"
                        >
                          <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
                        </div>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Add to ${list.name}`}
                          onClick={(e) => void handleAddListClick(e, list)}
                          className="flex h-6 w-6 shrink-0 items-center justify-center text-[#252528] transition-colors hover:text-[#371168] active:scale-95 disabled:opacity-40"
                          disabled={locked}
                        >
                          <AddCircleOutlineIcon sx={{ fontSize: 24 }} />
                        </button>
                      )}
                    </div>
                  );
                })
              ) : searchQuery ? (
                <div className="flex items-center justify-center py-10 text-sm text-[#a7a7ae]">
                  <span>No lists found</span>
                </div>
              ) : (
                <div className="flex items-center justify-center py-10 text-sm text-[#a7a7ae]">
                  <span>No lists yet. Create your first one!</span>
                </div>
              )}
            </div>
          ) : null}

          <div
            aria-hidden
            className={`pointer-events-none sticky bottom-0 z-[2] mt-[-44px] flex h-[44px] items-end justify-center gap-1 bg-gradient-to-b from-transparent via-white/90 to-white pb-2 transition-opacity ${
              showListPanelScrollHint ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#6e6e73' }} />
            <span className="text-xs font-medium text-[#525257]">Scroll for more</span>
          </div>
          <div ref={listPanelSentinelRef} className="h-px w-full shrink-0" aria-hidden />
        </div>
      ) : null}
    </div>
  );
};

export default SaveDestinationPickerPanel;
